import { observable }              from 'mobx';
import { runInAction }             from 'mobx';
import appStore                    from '@/stores/appStore';
import { limit }                   from './engine/types';
import { feature }                 from './engine/types';
import { unconfigured }            from './engine/types';
import { FunctionalityEntry }      from './engine/types';
import type AccountModel           from '@/models/services/account/AccountModel';
import type ProjectModel           from '@models/map/ProjectModel';
import type MapModel               from '@models/map/MapModel';
import type PlaceModel             from '@models/map/PlaceModel';
import type FieldModel             from '@models/map/FieldModel';
import type UserAccountModel       from '@models/account/UserAccountModel';
import type StonecodeFileFileModel from '@models/file/StonecodeFileFileModel';

/**
 * Les classes de modèles sont chargées via import() dynamique : un import statique créerait
 * un cycle modèles → stores éditeur → leftSidebarStore → sidebars → FunctionalityGate → dictionnaire,
 * et @doc.model lirait une classe pas encore initialisée (TDZ "Cannot access 'X' before initialization").
 * Le cache est observable pour que les gates attendent son remplissage (quelques microtasks au bootstrap).
 */
const models = observable({
	FieldModel: undefined as typeof FieldModel | undefined,
	MapModel: undefined as typeof MapModel | undefined,
	PlaceModel: undefined as typeof PlaceModel | undefined,
	ProjectModel: undefined as typeof ProjectModel | undefined,
	StonecodeFileFileModel: undefined as typeof StonecodeFileFileModel | undefined,
	UserAccountModel: undefined as typeof UserAccountModel | undefined,
}, {}, { deep: false });

// Exporté pour que les tests attendent ces imports avant de démarrer.
export const modelsLoaded = Promise.all([
	import('@models/map/FieldModel').then(m => runInAction(() => models.FieldModel = m.default)),
	import('@models/map/MapModel').then(m => runInAction(() => models.MapModel = m.default)),
	import('@models/map/PlaceModel').then(m => runInAction(() => models.PlaceModel = m.default)),
	import('@models/map/ProjectModel').then(m => runInAction(() => models.ProjectModel = m.default)),
	import('@models/file/StonecodeFileFileModel').then(m => runInAction(() => models.StonecodeFileFileModel = m.default)),
	import('@models/account/UserAccountModel').then(m => runInAction(() => models.UserAccountModel = m.default)),
]);

/**
 * Source de vérité des fonctionnalités d'abonnement : pour chaque référence, sa nature
 * (feature ou limite) et, pour les limites, comment compter l'utilisation courante.
 * Les scopes et comptages reproduisent les checks de validation du backend (méthodes
 * `check*` des AccountSubscriptionFunctionalityCheckerService de chaque service) :
 * toute nouvelle entrée doit s'aligner sur le check backend correspondant.
 */
export const FUNCTIONALITIES = {
	advanced_statistic: feature,
	archive_project: feature,
	auth_map: feature,
	basic_statistic: feature,
	create_tpl_project: feature,
	custom_css: feature,
	custom_sub_domain: feature,
	geolocalisation: feature,
	iframe_limit: unconfigured({ unit: { many: 'iframes', one: 'iframe' } }),
	// Le backend ne compte que les cartes actives du projet (checkMapLimit : MapStatus::ENABLED = 1).
	map_limit: limit(() => models.MapModel, {
		scope: 'project',
		unit: { many: 'cartes', one: 'carte' },
		filters: (project: ProjectModel) => project.id ? { project: project.id, mapStatus: '1' } : undefined,
	}),
	map_nesting: feature,
	// Pas de check backend (service file), scope 'account' par choix coté frontend.
	nb_images: limit(() => models.StonecodeFileFileModel, {
		scope: 'account',
		unit: { many: 'images', one: 'image' },
		filters: () => {
			const urns = appStore.accountMapUrns;

			if (!urns) return undefined;

			return {
				// Tableau vide interdit (filtre vide = toute la base côté service file) :
				// un account sans map n'a aucune image, on compte 0 via un urn impossible.
				'typeSource.source.entityUrn': urns.length ? urns : ['$:map:map:0' as Urn],
				'typeSource.type.reference': 'map_image' as const,
			};
		},
	}),
	// Le backend compte les champs du projet, à la création d'un champ (checkFieldLimit, service map).
	place_field_limit: limit(() => models.FieldModel, {
		scope: 'project',
		unit: { many: 'champs personnalisés', one: 'champ personnalisé' },
		filters: (project: ProjectModel) => project.id ? { project: project.id } : undefined,
	}),
	place_import: feature,
	// Le backend ne compte que les lieux actifs du projet (checkPlaceLimit : PlaceStatus::ENABLED = 1).
	place_limit: limit(() => models.PlaceModel, {
		scope: 'project',
		unit: { many: 'lieux', one: 'lieu' },
		filters: (project: ProjectModel) => project.id ? { project: project.id, placeStatus: '1' } : undefined,
	}),
	place_path: feature,
	// Le backend ne compte que les projets actifs (checkProjectLimit : ProjectStatus::ENABLED = 1).
	project_limit: limit(() => models.ProjectModel, {
		scope: 'account',
		unit: { many: 'projets', one: 'projet' },
		filters: (account: AccountModel) => account.urn ? { 'owner.accountUrn': account.urn, 'projectStatus': '1' } : undefined,
	}),
	support_enabled: feature,
	use_mediatech: feature,
	// Le backend compte les membres actifs du compte, administrateur compris (checkAccountUserLimit).
	user_limit: limit(() => models.UserAccountModel, {
		scope: 'account',
		unit: { many: 'utilisateurs', one: 'utilisateur' },
		filters: (account: AccountModel) => ({ 'account': account.id, 'userAccountStatus': '1' }),
	}),
	white_label: feature,
} satisfies Record<Account_FunctionalityReference, { type: FunctionalityEntry['type'] }>;
